// ✅ Trekkin.app — useGroupStore.jsx v4.1.0
// --------------------------------------------------------
// 🔹 Base = v3.9.1 (NADA removido, só acrescentei pequenos FIXES)
// 🔹 Mantém TUDO:
//    • Listener único para /groups
//    • mygroups/myGroups = grupos onde o user é membro
//    • Admin/Guide vê TODOS os grupos via `groups`
//    • joinGroup idempotente + normalização currentGroup { id, ... }
//    • Convites, inviteCode, deleteGroup, etc.
//    • memberLocations + listener dedicado + updateMyLocationInGroup
//    • leaveGroup remove também a localização e membership do próprio user
// 🔹 v4.0.0:
//    • ensureMemberInGroup(groupId, uid)  → força membro no grupo (para eventos)
//    • ensureNotMemberInGroup(groupId, uid) → remove membro + localização
//      (usado pelo PanelEventManage para o toggle add/remove)
// 🔹 v4.1.0:
//    • clearMemberLocation(groupId, uid) → limpa só a localização de um membro
//      (usado pelo MapView quando o guia escolhe ficar oculto no LIVE)
// --------------------------------------------------------

import { create } from "zustand";
import {
  ref,
  get as dbGet,
  onValue,
  off,
  push,
  set as firebaseSet,
  update,
  remove,
} from "firebase/database";
import { db } from "../firebaseConfig";
import { useAuthStore } from "./useAuthStore";
import { logEvent } from "./useLogsStore";
import { listenRtdbListIncremental } from "../utils/rtdbIncrementalList";
import { useActivityModeStore } from "./useActivityModeStore";
import { isActivityModeCompatible, normalizeActivityMode } from "../utils/activityMode";

const groupsCacheKey = (uid) => `trekkin.groups.cache.v1.${uid}`;

function readGroupsCache(uid) {
  try {
    const value = JSON.parse(localStorage.getItem(groupsCacheKey(uid)) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function writeGroupsCache(uid, groups) {
  try {
    localStorage.setItem(
      groupsCacheKey(uid),
      JSON.stringify(Array.isArray(groups) ? groups : [])
    );
  } catch {}
}

// Helper para códigos de convite
const LETTERS = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const DIGITS = "23456789";

function makeInviteCode() {
  const partA = Array.from({ length: 4 })
    .map(() => LETTERS[Math.floor(Math.random() * LETTERS.length)])
    .join("");
  const partB = Array.from({ length: 4 })
    .map(() => DIGITS[Math.floor(Math.random() * DIGITS.length)])
    .join("");
  return `${partA}-${partB}`;
}

function stripVolatileGroupFields(group = {}) {
  if (!group || typeof group !== "object") return {};
  const { memberLocations: _ignored, ...rest } = group;
  return rest;
}

function sameObjectKeysAndValues(a = {}, b = {}) {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (a[key] !== b[key]) return false;
  }
  return true;
}

function sameGroupSnapshot(prev = {}, next = {}) {
  if (!prev || !next) return false;

  const prevMembers = prev.members || {};
  const nextMembers = next.members || {};
  if (!sameObjectKeysAndValues(prevMembers, nextMembers)) return false;

  const prevBase = stripVolatileGroupFields(prev);
  const nextBase = stripVolatileGroupFields(next);
  delete prevBase.members;
  delete nextBase.members;

  return JSON.stringify(prevBase) === JSON.stringify(nextBase);
}

function visibleGroupsForUser(groups, uid, role) {
  const activeMode = useActivityModeStore.getState().activityMode;
  const compatible = groups.filter((group) => isActivityModeCompatible(group?.activityMode, activeMode));
  if (["admin", "guide"].includes(String(role || "").toLowerCase())) return compatible;
  return compatible.filter((group) => group?.eventPublished !== false || group?.members?.[uid]);
}

export const useGroupStore = create((set, get) => ({
  // ---------------------------
  // Estado base de grupos
  // ---------------------------
  groups: [],
  rawGroups: [],
  mygroups: [],
  myGroups: [],
  currentGroup: null, // objeto { id, ... }

  groupsListenerRef: null,
  isLoadingGroups: false,

  // ---------------------------
  // NOVO — localização membros
  // ---------------------------
  memberLocations: {}, // { [gid]: { [uid]: { lat, lon, acc, ts, name } } }
  locationListenerRef: null, // { gid, ref }
  lastLocationSentAt: 0,

  // 🔔 UNREAD CHAT COUNTERS
  unreadGlobal: 0,
  unreadGroup: 0,

  loadGroupsOnce: async () => {
    const authUser = useAuthStore.getState().user;
    const uid = authUser?.uid || null;
    if (!uid) return [];

    const cachedGroups = readGroupsCache(uid);
    if (cachedGroups.length) {
      const cachedMine = cachedGroups.filter((group) => group?.members?.[uid] && isActivityModeCompatible(group?.activityMode, useActivityModeStore.getState().activityMode));
      const cachedVisible = visibleGroupsForUser(cachedGroups, uid, authUser?.role);
      set({
        rawGroups: cachedGroups,
        groups: cachedVisible,
        mygroups: cachedMine,
        myGroups: cachedMine,
        isLoadingGroups: false,
      });
    } else {
      set({ isLoadingGroups: true });
    }

    try {
      const snap = await dbGet(ref(db, "/groups"));
      const raw = snap.val() || {};
      const arr = Object.entries(raw).map(([id, v]) => {
        const { memberLocations: _ignoredMemberLocations, ...rest } = v || {};
        const safeMembers = v?.members
          ? Object.fromEntries(
              Object.entries(v.members).filter(
                ([, val]) => val !== null && val !== false
              )
            )
          : {};
        return {
          id,
          ...rest,
          members: safeMembers,
        };
      });

      const my = arr.filter((g) => g.members?.[uid] && isActivityModeCompatible(g?.activityMode, useActivityModeStore.getState().activityMode));
      const visible = visibleGroupsForUser(arr, uid, authUser?.role);

      writeGroupsCache(uid, arr);

      set({
        rawGroups: arr,
        groups: visible,
        mygroups: my,
        myGroups: my,
        isLoadingGroups: false,
      });

      logEvent("groups_load_once", "Snapshot /groups carregado uma vez", {
        total: arr.length,
        mine: my.length,
        uid,
      });

      return arr;
    } catch (e) {
      logEvent("groups_load_once_error", "Erro ao carregar /groups uma vez", {
        error: e?.message || String(e),
        uid,
      });
      set({ isLoadingGroups: false });
      return [];
    }
  },

  // 📡 Ouvir todos os grupos (listener único)
  listenGroups: () => {
    const { groupsListenerRef } = get();
    if (groupsListenerRef) {
      logEvent("groups_listen_skip", "listenGroups ignorado — já ativo");
      return;
    }

    const authUser = useAuthStore.getState().user;
    const uid = authUser?.uid || null;
    if (!uid) return;

    const cachedGroups = readGroupsCache(uid);
    if (cachedGroups.length && !(get().groups || []).length) {
      const cachedMine = cachedGroups.filter((group) => group?.members?.[uid] && isActivityModeCompatible(group?.activityMode, useActivityModeStore.getState().activityMode));
      set({
        rawGroups: cachedGroups,
        groups: visibleGroupsForUser(cachedGroups, uid, authUser?.role),
        mygroups: cachedMine,
        myGroups: cachedMine,
        isLoadingGroups: false,
      });
    }

    const r = ref(db, "/groups");
    logEvent("groups_listen_start", "A ouvir /groups", { path: "/groups" });

    set({ groupsListenerRef: r, isLoadingGroups: true });

    const stop = listenRtdbListIncremental(r, {
      mapItem: (id, v = {}) => {
        const { memberLocations: _ignoredMemberLocations, ...rest } = v || {};
        const safeMembers = v.members
          ? Object.fromEntries(
              Object.entries(v.members).filter(
                ([, val]) => val !== null && val !== false
              )
            )
          : {};

        return {
          id,
          ...rest,
          members: safeMembers,
        };
      },
      isItemEqual: sameGroupSnapshot,
      onReady: () => {
        set({ isLoadingGroups: false });
      },
      onData: (arr) => {
        const authUser = useAuthStore.getState().user;
        const uid = authUser?.uid || null;
        const my = uid ? arr.filter((g) => g.members?.[uid] && isActivityModeCompatible(g?.activityMode, useActivityModeStore.getState().activityMode)) : [];
        const visible = visibleGroupsForUser(arr, uid, authUser?.role);

        if (uid) writeGroupsCache(uid, arr);

        logEvent("groups_snapshot", "Snapshot /groups recebido", {
          total: arr.length,
          mine: my.length,
          uid,
        });

        set({
          rawGroups: arr,
          groups: visible,
          mygroups: my,
          myGroups: my,
          isLoadingGroups: false,
        });
      },
      onError: (e) => {
        logEvent("groups_listen_error", "Erro ao ouvir /groups", {
          error: e?.message || String(e),
        });
        set({ isLoadingGroups: false });
      },
    });

    set({ groupsListenerRef: { ref: r, stop } });
  },

  // 📴 Parar listener de grupos
  stopListeningGroups: () => {
    const { groupsListenerRef } = get();
    if (groupsListenerRef) {
      if (typeof groupsListenerRef.stop === "function") {
        groupsListenerRef.stop();
      } else if (groupsListenerRef.ref) {
        off(groupsListenerRef.ref);
      } else {
        off(groupsListenerRef);
      }
      logEvent("groups_listen_stop", "Listener /groups parado");
      set({ groupsListenerRef: null });
    }
  },

  // --------------------------------------------------------
  // 🔎 Helper — grupo por id
  // --------------------------------------------------------
  getGroupById: (gid) => {
    const { groups } = get();
    return (groups || []).find((grp) => grp.id === gid) || null;
  },

  // --------------------------------------------------------
  // 🌍 Listener de localização do grupo atual
  // --------------------------------------------------------
  _startLocationListener: (gid) => {
    if (!gid) return;

    const { locationListenerRef } = get();

    // Já a ouvir este grupo
    if (locationListenerRef && locationListenerRef.gid === gid) {
      logEvent(
        "groups_loc_listen_skip",
        "Listener memberLocations já para este grupo",
        { gid }
      );
      return;
    }

    // Desmontar anterior, se existir
    if (locationListenerRef?.stop) {
      locationListenerRef.stop();
      logEvent("groups_loc_listen_stop", "Listener memberLocations parado", {
        gid: locationListenerRef.gid,
      });
    } else if (locationListenerRef?.ref) {
      off(locationListenerRef.ref);
      logEvent("groups_loc_listen_stop", "Listener memberLocations parado", {
        gid: locationListenerRef.gid,
      });
    }

    const r = ref(db, `/groups/${gid}/memberLocations`);
    logEvent(
      "groups_loc_listen_start",
      "A ouvir localização dos membros do grupo",
      { gid, path: `/groups/${gid}/memberLocations` }
    );

    const stop = listenRtdbListIncremental(r, {
      mapItem: (id, loc) => ({ id, ...(loc || {}) }),
      onData: (arr) => {
        const safeLocations = {};
        arr.forEach((item) => {
          if (
            item &&
            Number.isFinite(item.lat) &&
            Number.isFinite(item.lon)
          ) {
            safeLocations[item.id] = {
              lat: item.lat,
              lon: item.lon,
              acc: Number.isFinite(item.acc) ? item.acc : 0,
              ts: Number(item.ts || 0) || Date.now(),
              name: item.name || "",
            };
          }
        });

        const count = Object.keys(safeLocations).length;

        set((state) => ({
          memberLocations: {
            ...state.memberLocations,
            [gid]: safeLocations,
          },
        }));

        logEvent("groups_loc_snapshot", "Snapshot memberLocations recebido", {
          gid,
          count,
        });
      },
      onError: (e) => {
        logEvent(
          "groups_loc_listen_error",
          "Erro ao ouvir memberLocations",
          {
            gid,
            error: e?.message || String(e),
          }
        );
      },
    });

    set({ locationListenerRef: { gid, ref: r, stop } });
  },

  _stopLocationListener: () => {
    const { locationListenerRef } = get();
    if (locationListenerRef?.stop) {
      locationListenerRef.stop();
      logEvent(
        "groups_loc_listen_stop",
        "Listener memberLocations parado",
        { gid: locationListenerRef.gid }
      );
    } else if (locationListenerRef?.ref) {
      off(locationListenerRef.ref);
      logEvent(
        "groups_loc_listen_stop",
        "Listener memberLocations parado",
        { gid: locationListenerRef.gid }
      );
    }
    set({ locationListenerRef: null });
  },

  // --------------------------------------------------------
  // 📍 Atualizar MINHA localização no grupo
  // --------------------------------------------------------
  updateMyLocationInGroup: async ({ lat, lon, acc = 0 }) => {
    const state = get();
    const { currentGroup, lastLocationSentAt } = state;

    const user = useAuthStore.getState().user;
    const uid = user?.uid || null;

    const gid =
      typeof currentGroup === "string"
        ? currentGroup
        : currentGroup?.id ||
          currentGroup?.groupId ||
          currentGroup?.firebaseId ||
          currentGroup?.key ||
          null;

    if (
      !gid ||
      !uid ||
      !Number.isFinite(lat) ||
      !Number.isFinite(lon)
    ) {
      return;
    }

    const now = Date.now();
    // Throttle simples de ~4s
    if (now - lastLocationSentAt < 4000) return;

    // Nome → usernames > user.name (perfil) > displayName > email
    const authState = useAuthStore.getState();
    const fullNameRaw =
      authState.usernames?.[uid] ||
      user.name || // 👈 vem do PanelProfile / RTDB
      user.displayName ||
      user.email ||
      "Trekker";

    let fullName = (fullNameRaw || "Trekker").trim();

    // Se for email, corta antes do @
    const atIndex = fullName.indexOf("@");
    if (atIndex > 0) {
      fullName = fullName.slice(0, atIndex);
    }

    // Se tiver espaços, fica só o primeiro token
    const spaceIndex = fullName.indexOf(" ");
    if (spaceIndex > 0) {
      fullName = fullName.slice(0, spaceIndex);
    }

    const firstName = fullName.slice(0, 18) || "Trekker";

    const loc = {
      lat,
      lon,
      acc: Number.isFinite(acc) ? acc : 0,
      ts: now,
      name: firstName,
    };

    // Update imediato em memória
    set((prev) => ({
      lastLocationSentAt: now,
      memberLocations: {
        ...prev.memberLocations,
        [gid]: {
          ...(prev.memberLocations[gid] || {}),
          [uid]: loc,
        },
      },
    }));

    // Sync no RTDB
    try {
      await firebaseSet(ref(db, `groups/${gid}/memberLocations/${uid}`), loc);

      logEvent("groups_loc_update", "Localização atualizada no grupo", {
        gid,
        uid,
        lat,
        lon,
      });
    } catch (e) {
      logEvent(
        "groups_loc_update_error",
        "Erro ao atualizar localização no grupo",
        {
          gid,
          uid,
          error: e?.message || String(e),
        }
      );
    }
  },

  // --------------------------------------------------------
  // 🆕 Limpar localização de UM membro (sem mexer em members)
  //     → usado pelo MapView quando o guia escolhe esconder-se no LIVE
  // --------------------------------------------------------
  clearMemberLocation: async (groupId, uid) => {
    if (!groupId || !uid) return;

    try {
      await firebaseSet(ref(db, `groups/${groupId}/memberLocations/${uid}`), null);

      // Limpar também no estado local
      set((prev) => {
        const prevForGroup = prev.memberLocations?.[groupId] || {};
        const { [uid]: _removed, ...rest } = prevForGroup;
        return {
          memberLocations: {
            ...prev.memberLocations,
            [groupId]: rest,
          },
        };
      });

      logEvent(
        "groups_clear_member_loc",
        "Localização limpa para membro",
        { groupId, uid }
      );
    } catch (e) {
      logEvent(
        "groups_clear_member_loc_error",
        "Erro ao limpar localização do membro",
        { groupId, uid, error: e?.message || String(e) }
      );
    }
  },

  // --------------------------------------------------------
  // ➕ Criar grupo
  // --------------------------------------------------------
  createGroup: async (name) => {
    const { user } = useAuthStore.getState();

    if (!user) {
      logEvent(
        "groups_create_fail",
        "Tentativa de createGroup sem utilizador",
        { name }
      );
      throw new Error("Sem utilizador autenticado.");
    }

    const uid = user.uid;
    const email = user.email || null;

    // Nome do user → usernames > user.name (perfil) > displayName > email
    const authState = useAuthStore.getState();
    let fullName =
      authState.usernames?.[uid] ||
      user.name || // 👈 nome salvo no PanelProfile
      user.displayName ||
      email ||
      "Trekker";

    fullName = fullName.trim();

    // Se for email → corta antes do @
    const atIndex = fullName.indexOf("@");
    if (atIndex > 0) {
      fullName = fullName.slice(0, atIndex);
    }

    // Se tiver espaços → só o primeiro nome
    const spaceIndex = fullName.indexOf(" ");
    if (spaceIndex > 0) {
      fullName = fullName.slice(0, spaceIndex);
    }

    const firstName = fullName.slice(0, 18) || "Trekker";

    const baseRef = ref(db, "/groups");
    const newRef = push(baseRef);
    const gid = newRef.key;

    const payload = {
      name,
      activityMode: normalizeActivityMode(useActivityModeStore.getState().activityMode),
      createdAt: Date.now(),
      owner_email: email,
      owner_uid: uid,
      owner_name: firstName, // ← CORRETO
      members: { [uid]: true },
      invites: null,
      inviteCode: null,
      inviteCodeUpdatedAt: null,
    };

    logEvent("groups_create", "Criar grupo", {
      gid,
      uid,
      email,
      name,
    });

    await update(ref(db), { [`groups/${gid}`]: payload });

    logEvent("groups_create_success", "Grupo criado com sucesso", { gid });
    return gid;
  },

  // 🔔 Convite por utilizador (sistema antigo)
  inviteUserToGroup: async (groupId, targetUid) => {
    const { user } = useAuthStore.getState();
    if (!user) {
      logEvent(
        "groups_invite_fail",
        "inviteUserToGroup sem utilizador autenticado",
        { groupId, targetUid }
      );
      throw new Error("Sem utilizador autenticado.");
    }

    const now = Date.now();

    const updates = {
      [`groups/${groupId}/invites/${targetUid}`]: {
        status: "pending",
        invitedAt: now,
        invitedBy: user.uid,
      },
      [`userInvites/${targetUid}/${groupId}`]: {
        status: "pending",
        invitedAt: now,
        guideUid: user.uid,
        guideName: user.email || "Guia",
        groupId,
      },
    };

    await update(ref(db), updates);
    logEvent("groups_invite", "Convite enviado para grupo", {
      groupId,
      targetUid,
    });
  },

  // ➡️ Entrar em grupo (idempotente + auto-membro + listener localização)
  joinGroup: async (groupOrId) => {
    const { user } = useAuthStore.getState();
    const uid = user?.uid || null;

    const gid =
      typeof groupOrId === "string"
        ? groupOrId
        : groupOrId?.id ||
          groupOrId?.groupId ||
          groupOrId?.firebaseId ||
          groupOrId?.key ||
          null;

    if (!gid) {
      logEvent(
        "groups_join_fail",
        "joinGroup chamado sem groupId válido",
        { groupOrId }
      );
      return;
    }

    const { currentGroup } = get();
    const currentId =
      typeof currentGroup === "string"
        ? currentGroup
        : currentGroup?.id ||
          currentGroup?.groupId ||
          currentGroup?.firebaseId ||
          currentGroup?.key ||
          null;

    if (currentId === gid) {
      logEvent("groups_join_skip", "joinGroup ignorado — já no mesmo grupo", {
        gid,
        uid,
      });
      // Mesmo assim garante listener de localização
      try {
        const startFn = get()._startLocationListener;
        if (typeof startFn === "function") {
          startFn(gid);
        }
      } catch (e) {
        console.warn("⚠️ _startLocationListener erro:", e);
      }
      return;
    }

    const state = get();
    const getGroupById =
      typeof state.getGroupById === "function"
        ? state.getGroupById
        : (id) => (state.groups || []).find((g) => g.id === id) || null;
    const fromState = getGroupById(gid);

    const normalized =
      fromState ||
      (typeof groupOrId === "object"
        ? { id: gid, ...groupOrId }
        : { id: gid });

    logEvent("groups_join", "Entrar no grupo", {
      gid,
      hasFromState: !!fromState,
      uid,
    });

    set({ currentGroup: normalized });

    // Garante membership no grupo (apenas admin/guide podem escrever)
    const role = useAuthStore.getState().user?.role?.toLowerCase() || "free";
    const canWriteMembers = role === "admin" || role === "guide";
    if (canWriteMembers && uid && (!fromState || !fromState.members?.[uid])) {
      try {
        await update(ref(db), {
          [`groups/${gid}/members/${uid}`]: true,
        });
        logEvent(
          "groups_join_member_added",
          "User adicionado como membro",
          { gid, uid }
        );
      } catch (e) {
        logEvent(
          "groups_join_member_error",
          "Erro ao adicionar membro ao grupo",
          {
            gid,
            uid,
            error: e?.message || String(e),
          }
        );
      }
    } else if (!canWriteMembers) {
      logEvent(
        "groups_join_member_skip",
        "joinGroup sem permissão para escrever members (role)",
        { gid, uid, role }
      );
    }

    // 👉 Começa a ouvir localização do grupo
    try {
      const startFn = get()._startLocationListener;
      if (typeof startFn === "function") {
        startFn(gid);
      } else {
        console.warn("⚠️ _startLocationListener missing", Object.keys(get()));
      }
    } catch (e) {
      console.warn("⚠️ _startLocationListener erro:", e);
    }
  },

  // ⬅️ Sair do grupo (limpa listener de localização + limpa RTDB)
  leaveGroup: async () => {
    const { currentGroup } = get();
    const user = useAuthStore.getState().user;
    const uid = user?.uid || null;

    const gid =
      typeof currentGroup === "string"
        ? currentGroup
        : currentGroup?.id ||
          currentGroup?.groupId ||
          currentGroup?.firebaseId ||
          currentGroup?.key ||
          null;

    logEvent("groups_leave", "Sair do grupo", { currentGroup, gid, uid });

    // ao sair, limpa também a tua membership + localização no RTDB
    if (gid && uid) {
      try {
        await update(ref(db), {
          [`groups/${gid}/members/${uid}`]: null,
          [`groups/${gid}/memberLocations/${uid}`]: null,
        });

        logEvent("groups_leave_cleanup", "Membership + localização limpas", {
          gid,
          uid,
        });
      } catch (e) {
        logEvent(
          "groups_leave_cleanup_error",
          "Erro ao limpar membership/localização ao sair",
          { gid, uid, error: e?.message || String(e) }
        );
      }
    }

    // Para listener de localização
    try {
      const stopFn = get()._stopLocationListener;
      if (typeof stopFn === "function") {
        stopFn();
      }
    } catch (e) {
      console.warn("⚠️ _stopLocationListener erro:", e);
    }

    set({ currentGroup: null });
  },

  // 👥 Remover membro (exceto dono)
  removeMember: async (groupId, memberUid) => {
    const { user } = useAuthStore.getState();
    if (!user) {
      logEvent(
        "groups_remove_fail",
        "removeMember sem utilizador autenticado",
        { groupId, memberUid }
      );
      throw new Error("Sem utilizador autenticado.");
    }

    const state = get();
    const getGroupById =
      typeof state.getGroupById === "function"
        ? state.getGroupById
        : (id) => (state.groups || []).find((g) => g.id === id) || null;
    const group = getGroupById(groupId);
    if (!group) {
      logEvent("groups_remove_fail", "Grupo não encontrado", {
        groupId,
        memberUid,
      });
      throw new Error("Grupo não encontrado.");
    }

    if (group.owner_uid && memberUid === group.owner_uid) {
      logEvent(
        "groups_remove_owner_block",
        "Tentativa de remover dono do grupo",
        { groupId, memberUid }
      );
      throw new Error("O dono do grupo não pode ser removido.");
    }

    await update(ref(db), {
      [`groups/${groupId}/members/${memberUid}`]: null,
      [`groups/${groupId}/memberLocations/${memberUid}`]: null,
    });

    logEvent("groups_remove", "Membro removido do grupo", {
      groupId,
      memberUid,
    });
  },

  // 🔐 Gerar (ou regenerar) código de convite
  generateInviteCode: async (groupId) => {
    const { user } = useAuthStore.getState();
    if (!user) {
      logEvent(
        "groups_invitecode_fail",
        "generateInviteCode sem utilizador autenticado",
        { groupId }
      );
      throw new Error("Sem utilizador autenticado.");
    }

    const state = get();
    const getGroupById =
      typeof state.getGroupById === "function"
        ? state.getGroupById
        : (id) => (state.groups || []).find((g) => g.id === id) || null;
    const group = getGroupById(groupId);
    if (!group) {
      logEvent(
        "groups_invitecode_fail",
        "Grupo não encontrado ao gerar código",
        { groupId }
      );
      throw new Error("Grupo não encontrado.");
    }

    if (group.owner_uid && group.owner_uid !== user.uid) {
      logEvent(
        "groups_invitecode_forbidden",
        "User tentou gerar código sem ser dono",
        { groupId, owner_uid: group.owner_uid, uid: user.uid }
      );
      throw new Error("Apenas o dono do grupo pode gerar o código.");
    }

    const code = makeInviteCode();
    const now = Date.now();

    await update(ref(db), {
      [`groups/${groupId}/inviteCode`]: code,
      [`groups/${groupId}/inviteCodeUpdatedAt`]: now,
    });

    logEvent("groups_invitecode_updated", "Código de convite atualizado", {
      groupId,
      code,
    });

    return code;
  },

  // Limpar código de convite
  clearInviteCode: async (groupId) => {
    await update(ref(db), {
      [`groups/${groupId}/inviteCode`]: null,
      [`groups/${groupId}/inviteCodeUpdatedAt`]: null,
    });

    logEvent("groups_invitecode_cleared", "Código de convite limpo", {
      groupId,
    });
  },

  // 🗑️ Apagar grupo
  deleteGroup: async (gid) => {
    logEvent("groups_delete", "A apagar grupo", { gid });

    try {
      await remove(ref(db, `groups/${gid}`));
      logEvent("groups_delete_success", "Grupo apagado com sucesso", { gid });
    } catch (e) {
      logEvent("groups_delete_error", "Erro ao apagar grupo", {
        gid,
        error: e?.message || String(e),
      });
      throw e;
    }
  },

  // --------------------------------------------------------
  // 🆕 Helpers para sync com eventos (toggle add/remove)
  // --------------------------------------------------------
  ensureMemberInGroup: async (groupId, uid) => {
    if (!groupId || !uid) return;
    const role =
      useAuthStore.getState().user?.role?.toLowerCase() || "free";
    if (role !== "admin" && role !== "guide") return;

    try {
      await update(ref(db), {
        [`groups/${groupId}/members/${uid}`]: true,
      });

      logEvent("groups_ensure_member", "User forçado como membro do grupo", {
        groupId,
        uid,
      });
    } catch (e) {
      logEvent(
        "groups_ensure_member_error",
        "Erro em ensureMemberInGroup",
        { groupId, uid, error: e?.message || String(e) }
      );
      throw e;
    }
  },

  ensureNotMemberInGroup: async (groupId, uid) => {
    if (!groupId || !uid) return;

    try {
      await update(ref(db), {
        [`groups/${groupId}/members/${uid}`]: null,
        [`groups/${groupId}/memberLocations/${uid}`]: null,
      });

      logEvent(
        "groups_ensure_not_member",
        "User removido de members + memberLocations",
        { groupId, uid }
      );
    } catch (e) {
      logEvent(
        "groups_ensure_not_member_error",
        "Erro em ensureNotMemberInGroup",
        { groupId, uid, error: e?.message || String(e) }
      );
      throw e;
    }
  },

  setActiveEventId: async (groupId, eventId) => {
  const role =
    useAuthStore.getState().user?.role?.toLowerCase() || "free";

  // 🔒 Só Admin/Guide podem escrever activeEventId no grupo
  if (role !== "admin" && role !== "guide") {
    logEvent(
      "groups_set_active_event_blocked",
      "setActiveEventId bloqueado por role",
      { groupId, eventId, role }
    );
    return;
  }

  try {
    await update(ref(db), {
      [`groups/${groupId}/activeEventId`]: eventId,
    });

    logEvent("groups_set_active_event", "activeEventId atualizado", {
      groupId,
      eventId,
      role,
    });
  } catch (e) {
    console.error("Erro ao definir activeEventId:", e);
    logEvent(
      "groups_set_active_event_error",
      "Erro ao atualizar activeEventId",
      {
        groupId,
        eventId,
        role,
        error: e?.message || String(e),
      }
    );
  }
},
}));

// Reaplica imediatamente o filtro quando o utilizador alterna entre
// caminhada e bicicleta, sem esperar por uma nova alteração no Firebase.
useActivityModeStore.subscribe((state, previous) => {
  if (state.activityMode === previous.activityMode) return;
  const authUser = useAuthStore.getState().user;
  const uid = authUser?.uid || null;
  const rawGroups = useGroupStore.getState().rawGroups || [];
  const my = uid
    ? rawGroups.filter((group) => group?.members?.[uid] && isActivityModeCompatible(group?.activityMode, state.activityMode))
    : [];
  useGroupStore.setState({
    groups: visibleGroupsForUser(rawGroups, uid, authUser?.role),
    mygroups: my,
    myGroups: my,
  });
});

// --------------------------------------------------------
// 🔔 UNREAD CHAT → BRIDGE GLOBAL (UMA VEZ)
// --------------------------------------------------------
if (typeof window !== "undefined" && !window.__trekkinUnreadBridge) {
  window.__trekkinUnreadBridge = true;

  window.addEventListener("trekkin:unread-global", (e) => {
    const unread = Number(e?.detail) || 0;
    useGroupStore.setState({ unreadGlobal: unread });
  });

  window.addEventListener("trekkin:unread-group", (e) => {
    const unread = Number(e?.detail?.unread) || 0;
    useGroupStore.setState({ unreadGroup: unread });
  });
}
